import { db, storage } from './firebase';
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { ArchitectStandupNote } from '../types';

const COLLECTION = 'architectStandupNotes';
const AUDIO_PREFIX = 'architectStandupAudio';

export const ARCHITECT_STANDUP_RECENT_LIMIT = 500;

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ArchitectStandupNote {
  const d = snap.data();
  return {
    id: snap.id,
    projectId: d.projectId as string,
    text: (d.text as string) || '',
    audioUrl: (d.audioUrl as string) || undefined,
    audioStoragePath: (d.audioStoragePath as string) || undefined,
    createdAt: (d.createdAt as { toDate: () => Date })?.toDate?.() ?? new Date(),
    createdBy: (d.createdBy as string) || '',
    createdByName: (d.createdByName as string) || 'Unknown',
  };
}

export async function uploadArchitectStandupAudio(
  projectId: string,
  blob: Blob,
): Promise<{ url: string; storagePath: string }> {
  const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
  const storagePath = `${AUDIO_PREFIX}/${projectId}/${Date.now()}.${ext}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, blob, { contentType: blob.type || 'audio/webm' });
  const url = await getDownloadURL(storageRef);
  return { url, storagePath };
}

export interface CreateStandupNoteInput {
  text: string;
  audioBlob?: Blob | null;
}

export async function createArchitectStandupNote(
  projectId: string,
  input: CreateStandupNoteInput,
  user: { id: string; displayName: string },
): Promise<ArchitectStandupNote> {
  const trimmed = input.text.trim();
  if (!trimmed && !input.audioBlob) {
    throw new Error('Add a sentence or record audio before saving.');
  }

  let audioUrl: string | undefined;
  let audioStoragePath: string | undefined;
  if (input.audioBlob && input.audioBlob.size > 0) {
    const uploaded = await uploadArchitectStandupAudio(projectId, input.audioBlob);
    audioUrl = uploaded.url;
    audioStoragePath = uploaded.storagePath;
  }

  const now = new Date();
  const payload: Record<string, unknown> = {
    projectId,
    text: trimmed,
    createdAt: Timestamp.fromDate(now),
    createdBy: user.id,
    createdByName: user.displayName,
  };
  if (audioUrl) payload.audioUrl = audioUrl;
  if (audioStoragePath) payload.audioStoragePath = audioStoragePath;

  const docRef = await addDoc(collection(db, COLLECTION), payload);

  return {
    id: docRef.id,
    projectId,
    text: trimmed,
    audioUrl,
    audioStoragePath,
    createdAt: now,
    createdBy: user.id,
    createdByName: user.displayName,
  };
}

export function subscribeArchitectStandupNotes(
  projectId: string,
  callback: (notes: ArchitectStandupNote[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.warn('[subscribeArchitectStandupNotes] listener failed:', err);
      onError?.(err);
      callback([]);
    },
  );
}

/** Recent notes across all projects — leadership All Projects latest-by-project hook. */
export function subscribeRecentArchitectStandupNotes(
  callback: (notes: ArchitectStandupNote[]) => void,
  maxDocs = ARCHITECT_STANDUP_RECENT_LIMIT,
): () => void {
  const q = query(
    collection(db, COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(maxDocs),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      console.warn('[subscribeRecentArchitectStandupNotes] listener failed:', err);
      callback([]);
    },
  );
}

export function latestArchitectStandupByProject(
  notes: ArchitectStandupNote[],
): Map<string, ArchitectStandupNote> {
  const map = new Map<string, ArchitectStandupNote>();
  for (const note of notes) {
    if (!note.projectId || map.has(note.projectId)) continue;
    map.set(note.projectId, note);
  }
  return map;
}
